import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import FlagIcon from '@/components/FlagIcon';
import { getStoredLanguage, setStoredLanguage, type SupportedLanguage } from '@/i18n/language';

type Props = {
  compact?: boolean;
  className?: string;
};

type LanguageOption = {
  lang: SupportedLanguage;
  label: string;
  countryCode: string;
};

const LANGUAGE_OPTIONS: LanguageOption[] = [
  { lang: 'en', label: 'English', countryCode: 'US' },
  { lang: 'es', label: 'Español', countryCode: 'ES' },
];

export default function LanguageSwitcher({ compact = false, className = '' }: Props) {
  const { i18n } = useTranslation();
  const [lang, setLang] = useState<SupportedLanguage>(() => getStoredLanguage());
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const next = getStoredLanguage();
    setLang(next);
    if (i18n.language !== next) {
      void i18n.changeLanguage(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = useMemo(
    () => LANGUAGE_OPTIONS.find((o) => o.lang === lang) ?? LANGUAGE_OPTIONS[0],
    [lang],
  );

  const apply = useCallback(
    (next: SupportedLanguage) => {
      setStoredLanguage(next);
      setLang(next);
      void i18n.changeLanguage(next);
      setOpen(false);
    },
    [i18n],
  );

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      data-tour-id="post-login-tour.language"
      className={['relative', className].join(' ')}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Change language, current: ${current.label}`}
        className={[
          'inline-flex items-center gap-1.5 border border-white/15 bg-black/20 backdrop-blur-md',
          'text-white transition-all duration-200 hover:border-white/25',
          compact ? 'h-8 px-2 rounded-[10px]' : 'h-10 px-2.5 rounded-[12px]',
        ].join(' ')}
        style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
      >
        <FlagIcon countryCode={current.countryCode} size={compact ? 'md' : 'lg'} />
        <span
          className={[
            'font-semibold text-white/90 truncate max-w-[72px]',
            compact ? 'text-[11px]' : 'text-xs',
          ].join(' ')}
        >
          {current.label}
        </span>
        <ChevronDown
          className={[
            'flex-shrink-0 text-white/60 transition-transform duration-200',
            compact ? 'w-3 h-3' : 'w-3.5 h-3.5',
            open ? 'rotate-180' : '',
          ].join(' ')}
          aria-hidden
        />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Select language"
          className={[
            'absolute top-full right-0 z-50 mt-1.5 min-w-[168px] overflow-hidden',
            'rounded-xl border border-white/15 bg-black/90 shadow-xl backdrop-blur-md',
          ].join(' ')}
          style={{ backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
        >
          {LANGUAGE_OPTIONS.map((opt) => {
            const active = lang === opt.lang;
            return (
              <button
                key={opt.lang}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => apply(opt.lang)}
                className={[
                  'flex w-full items-center gap-2.5 px-3 text-left transition-colors duration-150',
                  compact ? 'py-2' : 'py-2.5',
                  active ? 'bg-white/10' : 'hover:bg-white/10',
                ].join(' ')}
              >
                <FlagIcon countryCode={opt.countryCode} size="md" />
                <span className="flex-1 text-sm font-medium text-white">{opt.label}</span>
                {active && <Check className="h-4 w-4 flex-shrink-0 text-cyan-400" aria-hidden />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
